import type { Language } from '../types';

/**
 * #1967 — the landing page's media registry: every asset the hero and the Learn
 * More modal resolve, in one place, so a swap is a filename and nothing else.
 *
 * Delivery of record: `06-Marketing/ENTREGA-LANZAMIENTO_2026-09/
 * 02-Landing_ENTREGA-2026-09-05-APROBADO/`, brief
 * `00_BRIEF-PARA-SESION-DE-DEV_2026-09-09.md` (architect relay 2026-09-09).
 * That brief SUPERSEDES the 05-sep handoff, and the 98 s / 104 s cuts named
 * there are retired — they live under `99-Superseded_NO-PUBLICAR/` and must not
 * be referenced. The v4 pair below is the approved set.
 *
 * Cache behaviour, measured on production 2026-09-05 and unchanged: `/assets/*`
 * is served `cache-control: max-age=3600`, because the deployed Hosting config
 * is `infrastructure/firebase/firebase.json` (release.yml copies
 * `apps/web-landing/dist/*` on top of it) and that file declares no media rule.
 * `apps/web-landing/firebase.json` declares an images-immutable rule and is NOT
 * deployed — #1968. So a reused filename is stale for an hour, not a year; new
 * filenames are still the rule because they make the swap atomic and cost
 * nothing. Every name below is new, and `QUUL_general_video_v1.mp4` is deleted
 * in the same change that stops referencing it.
 */

/**
 * A single `<source>` candidate: where the file is, and what it actually is.
 *
 * The `type` carries the full RFC 6381 codec parameter, never a bare
 * `video/mp4`. Source selection only discriminates on the codecs parameter —
 * with a bare MIME type every browser answers "yes I play mp4", takes the first
 * source and never evaluates the second, which is the version of this that
 * genuinely does nothing. (Architect correction, 2026-09-09.)
 */
export interface VideoSource {
  src: string;
  type: string;
}

/**
 * Profile/level strings, measured from the delivered files with
 * `node scripts/mp4-codec-string.mjs <file>` — read out of each file's own
 * `avcC` box, not copied from a table. Two independent machines over two
 * transfer paths measured the same values (architect cross-check 2026-09-09).
 */
const CODECS = {
  es1080: 'avc1.640028', // High@L4.0
  en1080: 'avc1.640028', // High@L4.0
} as const;

const mp4 = (codec: string): string => `video/mp4; codecs="${codec}"`;

/**
 * The explainer cuts shown in the Learn More modal, per page language, highest
 * rendition first.
 *
 * Both v4 cuts run 144 s at 1080p. **There is deliberately no 720p entry yet**
 * — Brand has not rendered the compat pair, and shipping 1080p-only is the
 * founder's ruling of 2026-09-10: what is live today is the April cut making
 * claims the product does not satisfy, and a progressive enhancement for old
 * decoders is not a reason to keep that live. The current production video is
 * itself a single source with no fallback, so nothing regresses.
 *
 * When the 720p pair lands (spec: Main@3.1 → `avc1.4D401F`), each language
 * gains a second entry here and its measured string above. That is the whole
 * change — {@link renditionDefects} already rejects an unmeasured or
 * non-distinct addition, so the gap is guarded rather than merely noted.
 */
export const VIDEO_SOURCES: Record<Language, VideoSource[]> = {
  es: [{ src: '/assets/QUUL_Explicativo_v4_ES_144s_1080p.mp4', type: mp4(CODECS.es1080) }],
  en: [{ src: '/assets/QUUL_Explicativo_v4_EN_144s_1080p.mp4', type: mp4(CODECS.en1080) }],
};

/** A `type` carrying a real measured codec parameter, not a bare MIME type. */
const MEASURED_CODEC_TYPE = /^video\/mp4; codecs="avc1\.[0-9a-f]{6}"$/;

/**
 * The rendition falsifier, keyed on what is DECLARED rather than on a rendition
 * count.
 *
 * Two vocabularies, deliberately not one:
 * - **absent by design** — a language declares no 720p entry, so there is
 *   nothing to compare and nothing to measure. Passes, vacuously and correctly.
 * - **declared but unmeasured, or declared and non-distinct** — red.
 *
 * The distinction is what keeps this alive across the 720p gap. A guard deleted
 * to reach green and re-added later from memory is how a fail-closed check dies
 * by widening; this one simply has nothing to object to until a second
 * rendition is declared, and objects the moment one is declared badly.
 *
 * Non-distinct is a real failure mode, not a theoretical one: both 1080p cuts
 * are High@L4.0, so a 720p cut encoded at the same profile yields a
 * byte-identical `type`, the browser takes the first source it is told it can
 * play, and the fallback is decorative.
 *
 * **Known residual, named here rather than left to be discovered.** This guard
 * trusts DECLARATION. A file dropped into `public/assets/` that nothing
 * declares is invisible to it, and the companion test in `heroVideo.test.ts`
 * checks *declared → exists*, not *exists → declared*. The blast radius is an
 * unused file shipping in the bundle: bytes, no behaviour, nothing a visitor
 * can reach. It is the gap the absent-by-design vocabulary creates, and it is
 * deliberately open (architect, 2026-09-10).
 *
 * Closing it would mean asserting every file under `public/assets/` is
 * declared, which needs an allowlist immediately: `QUUL_Logo.png` is referenced
 * from `Header.tsx` and `Footer.tsx`, not from this module, and `favicon.svg`,
 * `legal/**` and `support/**` are referenced from nowhere in `src/` at all.
 * That trades a gap whose worst case is a wasted download for an allowlist that
 * goes stale silently — a worse guard wearing a better one's clothes.
 *
 * @returns one human-readable line per defect; empty means the map is sound.
 */
export function renditionDefects(
  sources: Record<Language, VideoSource[]> = VIDEO_SOURCES
): string[] {
  const defects: string[] = [];
  for (const [language, renditions] of Object.entries(sources)) {
    for (const rendition of renditions) {
      if (!MEASURED_CODEC_TYPE.test(rendition.type)) {
        defects.push(`${language}: ${rendition.src} declares an unmeasured type "${rendition.type}"`);
      }
    }
    const types = renditions.map((rendition) => rendition.type);
    if (new Set(types).size !== types.length) {
      defects.push(`${language}: renditions share a codec string, so the lower one is unreachable`);
    }
  }
  return defects;
}

/**
 * Resolved when the page language has no cuts of its own.
 *
 * Spanish is the primary language of the page, so the ES list is the fallback.
 * It must always name files that exist: the previous fallback pointed at
 * `QUUL_general_video_v1.mp4`, which this change deletes.
 */
export const VIDEO_FALLBACK_SOURCES = VIDEO_SOURCES.es;

/**
 * Resolves the explainer sources for a page language, in preference order.
 *
 * `Record<Language, VideoSource[]>` makes the map total at compile time, so a
 * new language cannot be added without giving it sources. The fallback covers
 * the runtime case instead: `language` reaches here from App state across a
 * plain-JS boundary, and returning `undefined` would render a sourceless
 * `<video>`.
 */
export function resolveVideoSources(language: Language): VideoSource[] {
  return VIDEO_SOURCES[language] ?? VIDEO_FALLBACK_SOURCES;
}

/**
 * The hero. Founder decision 2026-09-09: the hero is the 8 s loop with the
 * still as its poster — not a static image. The poster is what a visitor sees
 * before the loop's first frame decodes, and it is also the whole hero for
 * anyone who has asked for reduced motion.
 *
 * Both are language-neutral: the hero carries no burnt-in copy.
 *
 * The explicativo files carry `v4`, so a revision gets a new URL for free. The
 * still does not — `QUUL_Hero_A3_1920x1080.webp` names a resolution, not a
 * version. **The day this still is revised it must ship under a new filename,
 * never the same path.** Marketing's name is kept verbatim for the first
 * delivery because nothing is live yet, so the first deploy is clean either
 * way, and renaming would sever the tree's link to the delivery register.
 *
 * Note on the staleness window, measured rather than assumed: the year-long
 * immutable rule for images lives in `apps/web-landing/firebase.json`, which is
 * NOT the deployed config — `release.yml` deploys hosting from
 * `infrastructure/firebase/`, whose header rules cover only the catch-all,
 * `*.html`, fonts and `js|css`. There is no image rule and no video rule there,
 * so media falls through to Firebase Hosting's `max-age=3600` default (#1968).
 * A reused name is therefore stale for an hour plus edge caching, not a year.
 * The new-filename rule stands regardless: it makes the swap atomic.
 */
export const HERO_LOOP_SRC = '/assets/QUUL_Hero_A3_animado_bucle_8s_1080p.mp4';
export const HERO_POSTER_SRC = '/assets/QUUL_Hero_A3_1920x1080.webp';

/**
 * Describes what the hero actually shows.
 *
 * #1967 R6 exists because the previous `alt` described a different photograph
 * than the one on the page. Writing this text without having seen the delivered
 * asset would reproduce that exact defect, so it is a placeholder until the
 * `.webp` is in the tree and can be looked at.
 *
 * Written 2026-09-09 from the delivered `.webp` after opening it, not from the
 * filename. It is English on a bilingual page, which is the same class as the
 * strings §1.4 records — that concern is R7, PARKED by the founder, and the
 * previous alt was English too, so this is not a regression and is not widened
 * here. Flagged rather than fixed.
 */
export const HERO_ALT =
  'Illustration of a woman with a rolling suitcase, seen from behind, facing a warm-toned city of shops, cafes and apartment buildings, with location pins marking places across the skyline';

/** True while {@link HERO_ALT} is still the placeholder. */
export const HERO_ALT_IS_PLACEHOLDER = HERO_ALT.startsWith('TODO-1967-R6');

/**
 * No `<track>` element is rendered for either cut. Founder decision (JL,
 * 2026-09-09): subtitles are not required on the page. The `.srt` files in the
 * delivery stay as source material and are not shipped.
 */
